import React, { useState } from "react";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import { apiList } from "../../../helpers/apiList";
import HeaderWithBreadcrumb from "../../../components/common/HeaderWithBreadcrumb";
import LeftCircle from "../../../assets/Icons/Left circle B.png";
import RightCircle from "../../../assets/Icons/Right circle A.png";
import { useSelector } from "react-redux";
import { MapPin } from "lucide-react";

const PincodeServiceability = () => {
  const dashboardData = useSelector((state) => state.balance.balance);
  const { userProfile } = useSelector((state) => state.auth);

  // ---------- Tab state ----------
  const [activeTab, setActiveTab] = useState("serviceability"); // "serviceability" | "addPincode"

  // ---------- Existing Serviceability tab state (unchanged) ----------
  const [pincode, setPincode] = useState("");
  const [serviceData, setServiceData] = useState(null);
  const [error, setError] = useState("");
  const [sourcePincode, setSourcePincode] = useState("");
  const [sourceError, setSourceError] = useState("");

  const normalizeApiData = (raw = {}) => {
    const yes = (v) => String(v ?? "").toLowerCase() === "yes";
    return {
      raw,
      prepaid: yes(raw.Prepaid),
      cod: yes(raw.COD),
      ndd: yes(raw.NDD_NextDayDelivery),
      reverse: yes(raw.Reverse),
      pickup: yes(raw.Pickup),
      dangerousGoods: yes(raw.DG_DangerousGoods),
      responseText: raw.response || raw.Response || "",
      source: raw.source || raw.Source || "",
      destination: raw.destination || raw.Destination || "",
    };
  };

  const handleChange = (e) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    if (value.length > 6) return;
    if (value.length === 1 && value === "0") return;
    setPincode(value);
    setError("");
  };

  const handleSourceChange = (e) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    if (value.length > 6) return;
    if (value.length === 1 && value === "0") return;

    setSourcePincode(value);
    setSourceError("");
  };

  const handleGetDetails = async () => {
    if (pincode.length !== 6) {
      setError("Please enter a valid 6-digit pincode.");
      setServiceData(null);
      return;
    }
    if (userProfile === "OPERATION") {
      if (sourcePincode.length !== 6) {
        setSourceError("Please enter a valid 6-digit source pincode.");
        return;
      }
    }

    const loggedInUserName = sessionStorage.getItem("username");

    try {
      const requestBody = {
        username: loggedInUserName,
        destination_pincode: pincode,
        source_pincode:
          userProfile === "OPERATION"
            ? sourcePincode
            : dashboardData?.wallet?.pincode,
      };

      const res = await axiosPostApi(
        `${apiList.PINCODE_SERVICEABILITY}`,
        requestBody,
      );

      const top = res?.data;
      const payload = top?.data;
      const data = res?.data?.data;

      const ok =
        (typeof top?.status !== "undefined" &&
          top.status === true &&
          top.respCode === "00") ||
        payload?.status === true;

      if (ok && payload) {
        // setServiceData(normalizeApiData(payload));
        setServiceData(data);
        setError("");
      } else {
        const serverMsg =
          top?.message ||
          payload?.response ||
          "No details found for this pincode.";
        setError(serverMsg);
        setServiceData(null);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch details. Please try again.");
      setServiceData(null);
    }
  };

  const handleReset = () => {
    setPincode("");
    setServiceData(null);
    setError("");
  };

  // ---------- "Add Pincode" tab state ----------
  const [addForm, setAddForm] = useState({
    pincode: "",
    city: "",
    state: "",
    country: "India",
  });
  const [addErrors, setAddErrors] = useState({});
  // "idle"  -> nothing looked up yet
  // "found" -> lookup found a match, fields prefilled for reference/correction
  // "none"  -> lookup found no match, fields blank/editable
  const [recordStatus, setRecordStatus] = useState("idle");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [addSubmitLoading, setAddSubmitLoading] = useState(false);
  // { type: "success" | "error", text: string }
  const [addApiMessage, setAddApiMessage] = useState(null);

  const handleAddFormPincodeChange = (e) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    if (value.length > 6) return;
    if (value.length === 1 && value === "0") return;

    setAddForm((prev) => ({ ...prev, pincode: value }));
    setAddErrors((prev) => ({ ...prev, pincode: "" }));
    setAddApiMessage(null);

    // Reset the lookup status any time the pincode is edited again
    if (recordStatus !== "idle") {
      setRecordStatus("idle");
    }
  };

  const handleAddFormFieldChange = (field) => (e) => {
    setAddForm((prev) => ({ ...prev, [field]: e.target.value }));
    setAddErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAddPincodeReset = () => {
    setAddForm({ pincode: "", city: "", state: "", country: "India" });
    setAddErrors({});
    setRecordStatus("idle");
    setAddApiMessage(null);
  };

  // Looks up an existing pincode record via PINCODE_LOOKUP (/master/optn/pincode-details)
  const handleLookup = async () => {
    if (addForm.pincode.length !== 6) {
      setAddErrors((prev) => ({
        ...prev,
        pincode: "Please enter a valid 6-digit pincode.",
      }));
      return;
    }

    const loggedInUserName = sessionStorage.getItem("username");
    setLookupLoading(true);
    setAddApiMessage(null);

    try {
      const res = await axiosPostApi(apiList.PINCODE_LOOKUP, {
        username: loggedInUserName,
        pincode: addForm.pincode,
      });

      const payload = res?.data;
      const found =
        payload?.status === true && payload?.respCode === "00" && payload?.data;

      if (found) {
        const details = payload.data;
        setAddForm({
          pincode: details.pincode || addForm.pincode,
          city: details.city || "",
          state: details.state || "",
          country: details.country || "India",
        });
        setRecordStatus("found");
      } else {
        setAddForm((prev) => ({
          ...prev,
          city: "",
          state: "",
          country: "India",
        }));
        setRecordStatus("none");
      }
    } catch (err) {
      console.error(err);
      setAddApiMessage({
        type: "error",
        text: "Failed to look up pincode. Please try again.",
      });
    } finally {
      setLookupLoading(false);
    }
  };

  // Creates a new pincode record via PINCODE_ADD (/master/optn/pincode-create)
  const handleAddPincode = async () => {
    const nextErrors = {};
    if (addForm.pincode.length !== 6) {
      nextErrors.pincode = "Please enter a valid 6-digit pincode.";
    }
    if (!addForm.city.trim()) {
      nextErrors.city = "City is required.";
    }
    if (!addForm.state.trim()) {
      nextErrors.state = "State is required.";
    }
    if (!addForm.country.trim()) {
      nextErrors.country = "Country is required.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setAddErrors(nextErrors);
      return;
    }

    const loggedInUserName = sessionStorage.getItem("username");
    setAddSubmitLoading(true);
    setAddApiMessage(null);

    try {
      const requestBody = {
        username: loggedInUserName,
        pincode: addForm.pincode,
        cityName: addForm.city,
        state: addForm.state,
        country: addForm.country,
      };

      const res = await axiosPostApi(apiList.PINCODE_ADD, requestBody);
      const payload = res?.data;

      if (payload?.status === true && payload?.respCode === "00") {
        setAddApiMessage({
          type: "success",
          text: payload?.message || "Pincode added successfully.",
        });
        setAddErrors({});
      } else {
        setAddApiMessage({
          type: "error",
          text: payload?.message || "Failed to add pincode. Please try again.",
        });
      }
    } catch (err) {
      console.error(err);
      setAddApiMessage({
        type: "error",
        text: "Failed to add pincode. Please try again.",
      });
    } finally {
      setAddSubmitLoading(false);
    }
  };

  return (
    <div>
      <HeaderWithBreadcrumb
        title="Pincode Serviceability"
        breadcrumb="Master"
      />

      {/* Tab Switcher */}
      <div className="w-[90%] flex items-center gap-8 border-b border-gray-200 mb-4 px-1">
        <button
          onClick={() => setActiveTab("serviceability")}
          className={`pb-3 text-base font-semibold transition ${
            activeTab === "serviceability"
              ? "text-[#FF702B] border-b-2 border-[#FF702B]"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Serviceability
        </button>
        <button
          onClick={() => setActiveTab("addPincode")}
          className={`pb-3 text-base font-semibold transition ${
            activeTab === "addPincode"
              ? "text-[#FF702B] border-b-2 border-[#FF702B]"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Add Pincode
        </button>
      </div>

      {/* ---------------- Serviceability Tab (unchanged) ---------------- */}
      {activeTab === "serviceability" && (
        <div className="w-[90%] relative bg-white shadow-md rounded-xl p-6 flex flex-col md:flex-row gap-6 pr-6">
          {/* Decorative Circles */}
          <img
            src={LeftCircle}
            alt="Left Decoration"
            className="absolute left-0 bottom-0 w-[110px] h-[105px] z-0"
          />
          <img
            src={RightCircle}
            alt="Right Decoration"
            className="absolute right-0 top-0 w-[110px] h-[105px] z-0"
          />

          {/* Left Section */}
          <div className="w-full md:w-[40%] border rounded-xl p-5 relative">
            {/* Source Pincode */}
            {userProfile === "OPERATION" && (
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  Source Pincode <span className="text-red-500">*</span>
                </h2>

                <div className="relative w-3/4">
                  <i className="fa fa-map-marker absolute left-3 top-3 text-[#FF702B] text-sm"></i>
                  <input
                    type="text"
                    placeholder="Enter source pincode..."
                    value={sourcePincode}
                    onChange={handleSourceChange}
                    className={`w-full border rounded-lg pl-9 pr-4 py-2 focus:outline-none focus:ring-2           focus:ring-[#FF702B]
                    ${sourceError ? "border-red-400" : "border-gray-300"}`}
                  />
                </div>

                {sourceError && (
                  <p className="text-red-500 text-sm mt-1">{sourceError}</p>
                )}
              </div>
            )}

            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Destination Pincode<span className="text-red-500">*</span>
            </h2>

            {/* Input with orange icon */}
            <div className="relative w-3/4">
              <i className="fa fa-map-marker absolute left-3 top-3 text-[#FF702B] text-sm"></i>
              <input
                type="text"
                value={pincode}
                onChange={handleChange}
                placeholder="Enter pincode..."
                className="w-full border border-gray-300 rounded-lg pl-9 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF702B]"
              />
            </div>

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

            <div className="flex items-center gap-3 mt-5">
              <button
                onClick={handleReset}
                className="bg-gray-200 hover:bg-gray-300 text-[#FF702B] font-semibold px-2 py-2 w-[160px] rounded-lg shadow-sm border-1 border-[#FF702B] transition"
              >
                Reset
              </button>

              <button
                onClick={handleGetDetails}
                className="bg-[#FF702B] hover:bg-[#e8601f] text-white font-semibold px-2 py-2 w-[160px] rounded-lg shadow-sm transition"
              >
                Get Details
              </button>
            </div>
          </div>

          {/* Right Section */}
          <div className="w-full md:w-[60%] bg-[#FFF5EB] border rounded-xl p-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Services Available:
            </h2>

            {!serviceData ? (
              <p className="text-gray-500 text-sm">
                Enter a valid pincode and click "Get Details" to see available
                services.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-gray-700">
                {/* API Response */}
                {serviceData.responseText && (
                  <p className="flex items-baseline gap-2">
                    <i className="fa fa-database text-gray-700 text-sm"></i>
                    <span>
                      <strong>API Response:</strong> {serviceData.responseText}
                    </span>
                  </p>
                )}

                {/* Source */}
                {serviceData.source && (
                  <p className="flex items-baseline gap-2">
                    <i className="fa fa-flag text-gray-700 text-sm"></i>
                    <span>
                      <strong>Source:</strong> {serviceData.source}
                    </span>
                  </p>
                )}

                {/* Destination */}
                {serviceData.destination && (
                  <p className="flex items-baseline gap-2">
                    <i className="fa fa-location-arrow text-gray-700 text-sm"></i>
                    <span>
                      <strong>Destination:</strong> {serviceData.destination}
                    </span>
                  </p>
                )}

                {/* Pickup */}
                <p className="flex items-baseline gap-2">
                  <i className="fa fa-truck text-gray-700 text-sm"></i>
                  <span>
                    <strong>Pickup:</strong>{" "}
                    {serviceData?.delhivery?.serviceable ||
                    serviceData?.shypme?.serviceable
                      ? "Available"
                      : "Not Available"}
                  </span>
                </p>

                {/* Reverse Pickup */}
                <p className="flex items-baseline gap-2">
                  <i className="fa fa-exchange text-gray-700 text-sm"></i>
                  <span>
                    <strong>Reverse Pickup:</strong>{" "}
                    {serviceData?.shypme?.Reverse === "Yes"
                      ? "Available"
                      : "Not Available"}
                  </span>
                </p>

                {/* Prepaid */}
                <p className="flex items-baseline gap-2">
                  <i className="fa fa-credit-card text-gray-700 text-sm"></i>
                  <span>
                    <strong>Prepaid:</strong>{" "}
                    {serviceData?.shypme?.Prepaid === "Yes"
                      ? "Available"
                      : "Not Available"}
                  </span>
                </p>

                {/* NDD */}
                <p className="flex items-baseline gap-2">
                  <i className="fa fa-bolt text-gray-700 text-sm"></i>
                  <span>
                    <strong>NDD (Next Day Delivery):</strong>{" "}
                    {serviceData?.shypme?.NDD_NextDayDelivery === "Yes"
                      ? "Available"
                      : "Not Available"}
                  </span>
                </p>

                {/* Dangerous Goods */}
                <p className="flex items-baseline gap-2">
                  <i className="fa fa-exclamation-triangle text-gray-700 text-sm"></i>
                  <span>
                    <strong>Dangerous Goods:</strong>{" "}
                    {serviceData.dangerousGoods ? "Allowed" : "Not Allowed"}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------- Add Pincode Tab ---------------- */}
      {activeTab === "addPincode" && (
        <div className="w-[90%] relative bg-white shadow-md rounded-xl p-6 flex flex-col md:flex-row gap-6 pr-6">
          {/* Decorative Circles */}
          <img
            src={LeftCircle}
            alt="Left Decoration"
            className="absolute left-0 bottom-0 w-[110px] h-[105px] z-0"
          />
          <img
            src={RightCircle}
            alt="Right Decoration"
            className="absolute right-0 top-0 w-[110px] h-[105px] z-0"
          />

          {/* Left Section - Form */}
          <div className="w-full md:w-[45%] border rounded-xl p-5 relative">
            <h2 className="text-xl font-bold text-gray-800 mb-3">
              Add Pincode
            </h2>

            {/* Info banner */}
            <div className="bg-[#FFF5EB] border border-[#FFE0C2] rounded-lg px-4 py-3 mb-5 text-sm text-gray-700">
              Enter a 6-digit pincode. You can use <strong>Lookup</strong> to
              check details before saving, then click{" "}
              <strong>Add Pincode</strong> to save.
            </div>

            {/* API response banner (success/error) */}
            {addApiMessage && (
              <div
                className={`rounded-lg px-4 py-3 mb-4 text-sm ${
                  addApiMessage.type === "success"
                    ? "bg-green-50 border border-green-300 text-green-700"
                    : "bg-red-50 border border-red-300 text-red-600"
                }`}
              >
                {addApiMessage.text}
              </div>
            )}

            {/* Pincode */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Pincode <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-2.5 text-[#FF702B] w-4 h-4" />
                <input
                  type="text"
                  value={addForm.pincode}
                  onChange={handleAddFormPincodeChange}
                  placeholder="Enter 6-digit pincode"
                  className={`w-full border rounded-lg pl-9 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF702B]
                    ${
                      addErrors.pincode ? "border-red-400" : "border-gray-300"
                    }`}
                />
              </div>
              <p
                className={`text-xs mt-1 ${
                  addErrors.pincode ? "text-red-500" : "text-gray-400"
                }`}
              >
                {addErrors.pincode || "Enter a valid 6-digit pincode."}
              </p>
            </div>

            {/* City / State */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={addForm.city}
                  onChange={handleAddFormFieldChange("city")}
                  placeholder="Enter city"
                  className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF702B]
                    ${addErrors.city ? "border-red-400" : "border-gray-300"}`}
                />
                {addErrors.city && (
                  <p className="text-red-500 text-xs mt-1">{addErrors.city}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={addForm.state}
                  onChange={handleAddFormFieldChange("state")}
                  placeholder="Enter state"
                  className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF702B]
                    ${addErrors.state ? "border-red-400" : "border-gray-300"}`}
                />
                {addErrors.state && (
                  <p className="text-red-500 text-xs mt-1">{addErrors.state}</p>
                )}
              </div>
            </div>

            {/* Country */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Country <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={addForm.country}
                onChange={handleAddFormFieldChange("country")}
                placeholder="Enter country"
                className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF702B]
                  ${addErrors.country ? "border-red-400" : "border-gray-300"}`}
              />
              {addErrors.country && (
                <p className="text-red-500 text-xs mt-1">{addErrors.country}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddPincodeReset}
                className="bg-gray-200 hover:bg-gray-300 text-[#FF702B] font-semibold px-2 py-2 w-[130px] rounded-lg shadow-sm border-1 border-[#FF702B] transition"
              >
                Reset
              </button>

              <button
                onClick={handleLookup}
                disabled={lookupLoading}
                className="bg-white hover:bg-gray-50 text-[#FF702B] font-semibold px-2 py-2 w-[130px] rounded-lg shadow-sm border-1 border-[#FF702B] transition disabled:opacity-60"
              >
                {lookupLoading ? "Checking..." : "Lookup"}
              </button>

              <button
                onClick={handleAddPincode}
                disabled={addSubmitLoading}
                className="bg-[#FF702B] hover:bg-[#e8601f] text-white font-semibold px-2 py-2 w-[160px] rounded-lg shadow-sm transition disabled:opacity-60"
              >
                {addSubmitLoading ? "Saving..." : "Add Pincode"}
              </button>
            </div>
          </div>

          {/* Right Section - Record status */}
          <div className="w-full md:w-[55%] bg-[#FFF5EB] border rounded-xl p-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Record status
            </h2>

            {recordStatus === "idle" && (
              <p className="text-gray-600 text-sm">Enter a pincode to begin.</p>
            )}

            {recordStatus === "found" && (
              <p className="text-gray-700 text-sm">
                A record already exists for this pincode. Details have been
                filled in below for your reference.
              </p>
            )}

            {recordStatus === "none" && (
              <p className="text-gray-700 text-sm">
                No existing record found for this pincode. Fill in the details
                and click <strong>Add Pincode</strong> to save it.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PincodeServiceability;
